import { cn } from '@exam-room/utils';
import { useTranslation } from 'react-i18next';

import { BookmarkIcon } from '../../components';
import { type ExamFormInput } from '../../providers/new-exam';
import ExamOverviewList from '../exam-overview-list';

interface ExamOverviewProps {
  minified?: boolean;
  title?: string;
  onTileClick?: () => void;
  answer?: ExamFormInput;
}

export default function ExamOverview({ minified, title, onTileClick, answer }: ExamOverviewProps) {
  const { t } = useTranslation();
  const answeredCount = answer ? Object.values(answer).filter((q) => q?.answer ?? false).length : 0;
  const unansweredCount = answer
    ? Object.values(answer).filter((q) => !(q?.answer ?? false)).length
    : 0;
  const bookmarkedCount = answer
    ? Object.values(answer).filter((q) => q?.isBookmark ?? false).length
    : 0;

  const header = (
    <>
      <div
        className={cn(
          'flex items-start break-all',
          minified
            ? 'text-caption w-full flex-col justify-between gap-4'
            : 'text-body1 w-max justify-center',
        )}
      >
        {title ? <h3 className='line-clamp-2 text-xl break-all'>{title}</h3> : null}
        <div
          className={cn(
            'flex w-full shrink-0 items-center justify-between pt-1',
            !minified && 'gap-6',
          )}
        >
          {minified ? (
            <div className='flex items-center gap-2'>
              <div className='border-success relative grid size-5 place-items-center rounded-sm border'>
                0
              </div>
              <p>{t('examOverview.currentQuestion')}</p>
            </div>
          ) : (
            <div className='flex items-center gap-2'>
              <p className='text-body1'>{t('examOverview.unanswered')}</p>
              <div className='border-grey-300 relative grid size-10 place-items-center rounded-sm border'>
                {unansweredCount}
              </div>
              <p className='text-body1'>{t('examOverview.items')}</p>
            </div>
          )}
          {minified ? (
            <div className='flex items-center gap-2'>
              <div className='bg-success-300 text-success relative grid size-5 place-items-center rounded-sm'>
                0
              </div>
              <p>{t('examOverview.answeredQuestions')}</p>
            </div>
          ) : (
            <div className='flex items-center gap-2'>
              <p className='text-body1'>{t('examOverview.answered')}</p>
              <div className='bg-success-300 text-success relative grid size-10 place-items-center rounded-sm'>
                {answeredCount}
              </div>
              <p className='text-body1'>{t('examOverview.items')}</p>
            </div>
          )}
          {minified ? (
            <div className='flex items-center gap-2'>
              <BookmarkIcon className='text-primary text-base' />
              <p>{t('examOverview.bookmarkQuestion')}</p>
            </div>
          ) : (
            <div className='flex items-center gap-2'>
              <p className='text-body1'>{t('examOverview.bookmarked')}</p>
              <div className='border-grey-300 text-body1 relative grid size-10 place-items-center rounded-sm border'>
                {bookmarkedCount}
                <BookmarkIcon className='text-primary absolute top-0 -right-[5px] z-10 -translate-y-1/3 text-xl' />
              </div>
              <p className='text-body1'>{t('examOverview.items')}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className='flex flex-col items-center gap-3'>
      {header}
      <ExamOverviewList minified={minified} onTileClick={onTileClick} />
    </div>
  );
}
